import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';

export class RaceContestantPoints extends BaseObj {
    public ID: number;
    public __id: number;

    public Comment: string | null;
    public __comment: string | null;

    public EventID: number;
    public __eventid: number;

    public Points: number;
    public __points: number;

    public PointTypeID: number;
    public __pointtypeid: number;

    public RaceContestantID: number;
    public __racecontestantid: number;

    public StationRoleID: number | null;
    public __stationroleid: number | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        Comment?: string | null;
        EntityState?: EntityState;
        EventID?: number;
        Points?: number;
        PointTypeID?: number;
        RaceContestantID?: number;
        StationRoleID?: number | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<RaceContestantPoints>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('RaceContestantPoints tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.Comment = values.Comment ?? null;
        this.__comment = values.Comment ?? null;
        Object.defineProperty(this, 'Comment', {
            get(): string | null {
                return this.__comment;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Comment', this.__comment, value);
                this.__comment = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Points = values.Points ?? 0;
        this.__points = values.Points ?? 0;
        Object.defineProperty(this, 'Points', {
            get(): number {
                return this.__points;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('Points', this.__points, value);
                this.__points = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.PointTypeID = values.PointTypeID ?? 0;
        this.__pointtypeid = values.PointTypeID ?? 0;
        Object.defineProperty(this, 'PointTypeID', {
            get(): number {
                return this.__pointtypeid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('PointTypeID', this.__pointtypeid, value);
                this.__pointtypeid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceContestantID = values.RaceContestantID ?? 0;
        this.__racecontestantid = values.RaceContestantID ?? 0;
        Object.defineProperty(this, 'RaceContestantID', {
            get(): number {
                return this.__racecontestantid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('RaceContestantID', this.__racecontestantid, value);
                this.__racecontestantid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StationRoleID = values.StationRoleID ?? null;
        this.__stationroleid = values.StationRoleID ?? null;
        Object.defineProperty(this, 'StationRoleID', {
            get(): number | null {
                return this.__stationroleid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('StationRoleID', this.__stationroleid, value);
                this.__stationroleid = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"Comment": ${JSON.stringify(this.Comment)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"Points": ${JSON.stringify(this.Points)}` +
            ',' +
            `"PointTypeID": ${JSON.stringify(this.PointTypeID)}` +
            ',' +
            `"RaceContestantID": ${JSON.stringify(this.RaceContestantID)}` +
            ',' +
            `"StationRoleID": ${JSON.stringify(this.StationRoleID)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of RaceContestantPoints from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        Comment?: string | null;
        EventID?: number;
        Points?: number;
        PointTypeID?: number;
        RaceContestantID?: number;
        StationRoleID?: number | null;
        EntityState?: 'Added' | 'Unchanged';
    }): RaceContestantPoints {
        return new RaceContestantPoints(values);
    }

    public static _FromServerJson(json: any): RaceContestantPoints {
        return new RaceContestantPoints({
            ID: json.ID,
            Comment: json.Comment ?? '',
            EventID: json.EventID,
            Points: json.Points,
            PointTypeID: json.PointTypeID,
            RaceContestantID: json.RaceContestantID,
            StationRoleID: json.StationRoleID ?? null,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of RaceContestantPoints from another instance of RaceContestantPoints.
     * @param other The RaceContestantPoints you want to clone
     * @returns A cloned RaceContestantPoints
     */
    public static FromOther(other: RaceContestantPoints): RaceContestantPoints {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return RaceContestantPoints._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return RaceContestantPoints.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(...props: ('ID' | 'Comment' | 'EventID' | 'Points' | 'PointTypeID' | 'RaceContestantID' | 'StationRoleID')[]) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'RaceContestantPoints';
}
